'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { prefersReducedMotion } from '@/components/home/motion-hooks'

// Sticky table of contents; the section being read is marked as you scroll.

const READ_LINE = 140

export function ArticleToc({ headings }) {
  const [active, setActive] = useState(headings[0]?.id)

  useEffect(() => {
    let frame = null
    const update = () => {
      frame = null
      let current = headings[0]?.id
      for (const heading of headings) {
        const node = document.getElementById(heading.id)
        if (node && node.getBoundingClientRect().top <= READ_LINE) {
          current = heading.id
        }
      }
      setActive(current)
    }
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [headings])

  const jump = (event, id) => {
    const node = document.getElementById(id)
    if (!node) return
    event.preventDefault()
    node.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    })
    window.history.replaceState(null, '', `#${id}`)
    setActive(id)
  }

  return (
    <nav aria-label="Inhalt" className="flex flex-col gap-3">
      <p className="font-mono text-xs text-signal">Inhalt</p>
      <ol className="flex flex-col border-l border-rule">
        {headings.map((heading, i) => {
          const current = heading.id === active
          return (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                onClick={event => jump(event, heading.id)}
                aria-current={current ? 'location' : undefined}
                className={cn(
                  '-ml-px flex gap-3 border-l-2 py-1.5 pl-4 text-sm leading-snug transition-colors duration-200',
                  current
                    ? 'border-signal text-ink'
                    : 'border-transparent text-graphite hover:text-ink'
                )}
              >
                <span
                  className={cn(
                    'pt-px font-mono text-xs tabular-nums transition-colors',
                    current ? 'text-signal' : 'text-graphite/70'
                  )}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>{heading.text}</span>
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
